import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Button from "../../buttons/buttons";
import Input from "../../inputs/inputs";
import { Beat } from "../../../plugins/spinners.plugin";
import CLOSE from "../../../assets/images/icons/close.svg";

const initialInput = {
  provider: "",
  provider_reference: "",
  payout_reason: "",
};

/**
 * Records how an approved payout was paid.
 * @param {(payload: {provider, provider_reference, payout_reason?}) => void} onSubmit
 */
export default function MarkPayoutPaidModal({
  show,
  onClose,
  onSubmit,
  submitting,
}) {
  const [input, setInput] = useState(initialInput);

  // Start with empty fields each time the modal opens
  useEffect(() => {
    if (show) setInput(initialInput);
  }, [show]);

  const canSubmit =
    input.provider.trim().length > 0 &&
    input.provider_reference.trim().length > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const payload = {
      provider: input.provider.trim(),
      provider_reference: input.provider_reference.trim(),
    };
    // payout_reason is optional, so only send it when filled in
    if (input.payout_reason.trim()) {
      payload.payout_reason = input.payout_reason.trim();
    }
    onSubmit(payload);
  };

  return (
    <Transition.Root show={!!show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white dark:bg-[#121212] px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
                <div className="absolute top-3 right-3">
                  <button
                    type="button"
                    className="bg-[#ffeae5] rounded-md p-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <img src={CLOSE} alt="close" className="w-3 h-3" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4"
                  >
                    Pay Out
                  </Dialog.Title>

                  <Input.Label
                    title={"Provider"}
                    htmlFor={"provider"}
                    id={"provider"}
                    name={"provider"}
                    type={"text"}
                    autoComplete={"off"}
                    value={input.provider}
                    placeholder={"e.g. Paystack, Bank Transfer"}
                    onChange={handleChange}
                  />

                  <Input.Label
                    title={"Provider Reference"}
                    htmlFor={"provider_reference"}
                    id={"provider_reference"}
                    name={"provider_reference"}
                    type={"text"}
                    autoComplete={"off"}
                    value={input.provider_reference}
                    placeholder={"Transaction reference from the provider"}
                    onChange={handleChange}
                  />

                  <label
                    htmlFor="payout_reason"
                    className="block text-sm font-medium text-[#151515] dark:text-white mb-2"
                  >
                    Payout Reason{" "}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="payout_reason"
                    name="payout_reason"
                    rows={3}
                    value={input.payout_reason}
                    placeholder={"Any note about this payout"}
                    onChange={handleChange}
                    className="block w-full rounded-md border border-gray-300 bg-white dark:bg-transparent dark:text-white p-3 text-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600"
                  />

                  <div className="mt-5 flex gap-4">
                    <Button.Secondary
                      title="Cancel"
                      onClick={onClose}
                      className="w-full"
                    />
                    <Button.Primary
                      title={
                        submitting ? <Beat color={"#ffffff"} /> : "Mark as Paid"
                      }
                      type="submit"
                      disabled={!canSubmit || submitting}
                      className="w-full"
                    />
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
